import { useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Info, Pencil, Users, ChevronDown, Loader2 } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useProducerActivityLog, type ProducerActivityItem } from '@/hooks/useProducerActivityLog';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const PII_PATTERN = /@|(\+?\d[\d\s\-]{7,})/;

function sanitizeMessage(msg: string): string {
  if (PII_PATTERN.test(msg)) return 'Systemhändelse registrerad.';
  return msg;
}

function getIcon(item: ProducerActivityItem) {
  if (item.level === 'error') return { Icon: AlertTriangle, className: 'text-destructive' };
  if (item.level === 'warn') return { Icon: AlertCircle, className: 'text-amber-500' };
  if (item.type === 'lifecycle') return { Icon: CheckCircle, className: 'text-primary' };
  if (item.type === 'team') return { Icon: Users, className: 'text-muted-foreground' };
  if (item.type === 'content') return { Icon: Pencil, className: 'text-muted-foreground' };
  return { Icon: Info, className: 'text-muted-foreground' };
}

function ActivityRow({ item }: { item: ProducerActivityItem }) {
  const { Icon, className } = getIcon(item);
  const timeAgo = formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: sv });
  const safeMessage = sanitizeMessage(item.message);

  return (
    <div
      data-testid="producer-activity-row"
      className="flex items-start gap-3 py-2.5 px-1 border-b border-border/50 last:border-b-0"
    >
      <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', className)} />
      <div className="flex-1 min-w-0">
        <span data-testid="producer-activity-title" className="text-sm font-medium text-foreground">
          {item.title}
        </span>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{safeMessage}</p>
      </div>
      <div className="text-right shrink-0">
        <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo}</span>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {item.source === 'admin' && item.actor_user_id ? 'Admin' : 'System'}
        </p>
      </div>
    </div>
  );
}

interface ProducerActivityLogProps {
  producerId: string;
}

export function ProducerActivityLog({ producerId }: ProducerActivityLogProps) {
  const [limit, setLimit] = useState(10);
  const { data: items = [], isLoading } = useProducerActivityLog(producerId, limit);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} data-testid="producer-activity-log">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full flex items-center justify-between px-4 py-3 h-auto"
        >
          <span className="text-sm font-semibold text-foreground">Aktivitetslogg</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Ingen aktivitet registrerad ännu.
            </p>
          ) : (
            <>
              <div className="divide-y-0">
                {items.map((item) => (
                  <ActivityRow key={item.id} item={item} />
                ))}
              </div>
              {limit === 10 && items.length === 10 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2 text-xs"
                  onClick={() => setLimit(50)}
                >
                  Visa fler
                </Button>
              )}
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
